import type { IAPIRoutes } from "../../../../apiRoutes";
import type {
  CustomAssessmentCategory,
  ICreateCustomAssessmentTemplateRequest,
  IDeleteCustomAssessmentFormResponse,
  ICustomAssessmentFormResponse,
  ICustomAssessmentFormsEnvelope,
  ICustomAssessmentTemplate,
  IPutCustomAssessmentFormRequest,
  IUpdateCustomAssessmentTemplateRequest,
} from "../../../core/ports/driver/VisitNotesService";

export class CustomAssessmentApiError extends Error {
  code?: string;

  constructor(message: string, code?: string) {
    super(message);
    this.name = "CustomAssessmentApiError";
    this.code = code;
  }
}

const unwrap = (response: any) => response?.data ?? response;

function assertSuccess(response: any, fallback: string): any {
  const data = unwrap(response);
  const detail = data?.detail;
  if (
    data == null ||
    data?.success === false ||
    data?.error ||
    detail != null
  ) {
    const validationMessage = Array.isArray(detail)
      ? detail
          .map((item) => {
            const location = Array.isArray(item?.loc)
              ? item.loc.filter((part: unknown) => part !== "body").join(".")
              : "";
            return `${location ? `${location}: ` : ""}${
              item?.msg || "Invalid value"
            }`;
          })
          .join("; ")
      : undefined;
    const message =
      validationMessage ||
      (typeof detail === "string" ? detail : detail?.message) ||
      data?.error?.message ||
      data?.error ||
      data?.message ||
      fallback;
    const status = data?.status ?? data?.status_code ?? detail?.status;
    const code =
      data?.code ??
      data?.error?.code ??
      detail?.code ??
      (Array.isArray(detail) ? "validation" : undefined) ??
      (status === 409 ||
      /(?:changed while|stale|revision|concurrent|conflict)/i.test(message)
        ? "conflict"
        : undefined);
    throw new CustomAssessmentApiError(message, code);
  }
  return data;
}

const templatePath = (route: string, clinicId: number, templateId?: number) =>
  route
    .replace("{clinic_id}", String(clinicId))
    .replace("{template_id}", String(templateId ?? ""));

const responsePath = (
  route: string,
  clinicId: number,
  patientId: number,
  noteId: number,
  templateId?: number
) =>
  route
    .replace("{clinic_id}", String(clinicId))
    .replace("{patient_id}", String(patientId))
    .replace("{note_id}", String(noteId))
    .replace("{template_id}", String(templateId ?? ""));

export function createVisitNotesCustomAssessmentApi({
  GET,
  POST,
  PATCH,
  PUT,
  DELETE,
  routes,
}: {
  GET: IAPIProvider["GET"];
  POST: IAPIProvider["POST"];
  PATCH: IAPIProvider["PATCH"];
  PUT: IAPIProvider["PUT"];
  DELETE: IAPIProvider["DELETE"];
  routes: IAPIRoutes;
}) {
  const listCustomAssessmentTemplatesAPI = async (
    clinicId: number,
    category: CustomAssessmentCategory,
    includeArchived = false
  ): Promise<ICustomAssessmentTemplate[]> => {
    const response = await GET(
      templatePath(routes.apiAssessmentFormTemplates, clinicId),
      {
        category,
        ...(includeArchived ? { include_archived: true } : {}),
      }
    );
    const data = assertSuccess(response, "Failed to load assessment forms");
    const templates = data.templates;
    if (!Array.isArray(templates)) {
      throw new CustomAssessmentApiError(
        "Assessment form list response was invalid"
      );
    }
    return templates;
  };

  const getCustomAssessmentTemplateAPI = async (
    clinicId: number,
    templateId: number
  ): Promise<ICustomAssessmentTemplate> => {
    const response = await GET(
      templatePath(routes.apiAssessmentFormTemplate, clinicId, templateId)
    );
    const data = assertSuccess(response, "Failed to load assessment form");
    return data;
  };

  const createCustomAssessmentTemplateAPI = async (
    clinicId: number,
    request: ICreateCustomAssessmentTemplateRequest
  ): Promise<ICustomAssessmentTemplate> => {
    const response = await POST(
      templatePath(routes.apiAssessmentFormTemplates, clinicId),
      request
    );
    const data = assertSuccess(response, "Failed to create assessment form");
    return data;
  };

  const updateCustomAssessmentTemplateAPI = async (
    clinicId: number,
    templateId: number,
    request: IUpdateCustomAssessmentTemplateRequest
  ): Promise<ICustomAssessmentTemplate> => {
    const response = await PATCH(
      templatePath(routes.apiAssessmentFormTemplate, clinicId, templateId),
      request
    );
    const data = assertSuccess(response, "Failed to update assessment form");
    return data;
  };

  const archiveCustomAssessmentTemplateAPI = async (
    clinicId: number,
    templateId: number,
    baseRevision: number
  ): Promise<ICustomAssessmentTemplate> => {
    const response = await POST(
      templatePath(
        routes.apiArchiveAssessmentFormTemplate,
        clinicId,
        templateId
      ),
      { base_revision: baseRevision }
    );
    const data = assertSuccess(response, "Failed to archive assessment form");
    return data;
  };

  const publishCustomAssessmentTemplateAPI = async (
    clinicId: number,
    templateId: number,
    baseRevision: number
  ): Promise<ICustomAssessmentTemplate> => {
    const response = await POST(
      templatePath(
        routes.apiPublishAssessmentFormTemplate,
        clinicId,
        templateId
      ),
      { base_revision: baseRevision }
    );
    const data = assertSuccess(response, "Failed to publish assessment form");
    return data;
  };

  const getCustomAssessmentFormsAPI = async (
    clinicId: number,
    patientId: number,
    noteId: number
  ): Promise<ICustomAssessmentFormsEnvelope> => {
    const response = await GET(
      responsePath(
        routes.apiVisitNoteAssessmentForms,
        clinicId,
        patientId,
        noteId
      )
    );
    const data = assertSuccess(response, "Failed to load note assessments");
    if (
      data.schema_version !== 1 ||
      !data.forms ||
      typeof data.forms !== "object" ||
      Array.isArray(data.forms)
    ) {
      throw new CustomAssessmentApiError(
        "Note assessment response was invalid"
      );
    }
    return data;
  };

  const putCustomAssessmentFormAPI = async (
    clinicId: number,
    patientId: number,
    noteId: number,
    templateId: number,
    request: IPutCustomAssessmentFormRequest
  ): Promise<ICustomAssessmentFormResponse> => {
    const response = await PUT(
      responsePath(
        routes.apiVisitNoteAssessmentForm,
        clinicId,
        patientId,
        noteId,
        templateId
      ),
      request
    );
    const data = assertSuccess(response, "Failed to save note assessment");
    return data;
  };

  const deleteCustomAssessmentFormAPI = async (
    clinicId: number,
    patientId: number,
    noteId: number,
    templateId: number,
    baseRevision: number
  ): Promise<IDeleteCustomAssessmentFormResponse> => {
    const path = responsePath(
      routes.apiDeleteVisitNoteAssessmentForm,
      clinicId,
      patientId,
      noteId,
      templateId
    );
    const response = await DELETE(
      `${path}?base_revision=${encodeURIComponent(baseRevision)}`
    );
    const data = assertSuccess(response, "Failed to delete assessment draft");
    if (data.template_id !== templateId || data.deleted !== true) {
      throw new CustomAssessmentApiError(
        "Assessment draft deletion response was invalid"
      );
    }
    return data;
  };

  return {
    listCustomAssessmentTemplatesAPI,
    getCustomAssessmentTemplateAPI,
    createCustomAssessmentTemplateAPI,
    updateCustomAssessmentTemplateAPI,
    archiveCustomAssessmentTemplateAPI,
    publishCustomAssessmentTemplateAPI,
    getCustomAssessmentFormsAPI,
    putCustomAssessmentFormAPI,
    deleteCustomAssessmentFormAPI,
  };
}
